/**
 * The dossier header's figures, read the way the rest of the dossier reads
 * them, for the one metric bar every tab draws (see HEADER_GROUPS in
 * MetricBar.tsx).
 *
 * A figure is the deal's own value when it holds one (stated, or stored by the
 * engine — the lineage then marks it Derived). Where the deal holds nothing and
 * the calculation service computed the figure, that figure is shown with its
 * formula, so the tile reads Derived exactly as the Economics tab's does. A
 * subordinate note whose senior debt the record does not state shows no debt
 * yield ("n/a"), as Economics and Overview withhold it. Anything else missing
 * prints "—": nothing is filled in on a guess.
 */
import { formatAmount, formatDate, formatMultiple, formatPoints, textOrDash } from './format'
import { seniorAheadUnstated, type TermGetter } from './dealTerms'
import { ltvWithImplied } from './valueBasis'
import type { Calculated } from './leverageModel'
import { moneyWithCode } from '@/pages/pipeline-intelligence/currency'

/** The bar's figure slots, by catalogue field. */
const FIGURE_FIELDS = [
  'loan_amount',
  'fully_funded_amount',
  'ltv',
  'ltv_stabilized',
  'debt_yield',
  'debt_yield_stabilized',
  'dscr',
  'dscr_stabilized',
] as const

/** Amounts whose 0 means "not stated" (`loans.loan_amount` is NOT NULL; the
 *  derivation never writes a $0 commitment). */
const ZERO_IS_UNSTATED = new Set(['loan_amount', 'fully_funded_amount'])

/** What the Economics tab prints for a withheld note-only debt yield. */
export const DEBT_YIELD_WITHHELD = 'n/a'

function held(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

export function resolveHeaderFigures({
  get,
  calculated,
  capitalStack,
  product,
}: {
  /** The record's legacy product, read where no capital program is held. */
  product?: unknown
  /** The deal's own value of a catalogue field. */
  get: TermGetter
  /** The calculation service's figure for a field, if it computed one. */
  calculated: Calculated
  /** The deal's stored capital-stack layers (for the senior-debt test). */
  capitalStack?: unknown
}): { displays: Record<string, string>; derived: Record<string, string> } {
  const derived: Record<string, string> = {}
  const withheld = seniorAheadUnstated(get, capitalStack)

  const value = (name: string): unknown => {
    const own = get(name)
    const stated = ZERO_IS_UNSTATED.has(name) && Number(own) === 0 ? null : own
    if (held(stated)) return stated
    const computed = calculated(name)
    if (!computed) return null
    derived[name] = (computed.formula ?? 'computed from the deal’s figures').replace(/_/g, ' ')
    return computed.value
  }

  const currency = String(get('currency') ?? '').trim()
  const money = (name: string) => moneyWithCode(formatAmount(value(name)), currency)

  // The LTV the deal's loan and value imply, as the SERVER computed it and judged it
  // against its one tolerance (UI-03): the same answer for every role. Only where the
  // server returned nothing usable does the browser's own division stand.
  const ltvEntry = calculated('ltv')
  const servedImplied: number | null | undefined =
    ltvEntry?.matchesInputs === true
      ? null
      : ltvEntry?.matchesInputs === false && ltvEntry.fromInputs != null
        ? ltvEntry.fromInputs
        : undefined

  const figures: Record<(typeof FIGURE_FIELDS)[number], string> = {
    loan_amount: money('loan_amount'),
    fully_funded_amount: money('fully_funded_amount'),
    // The recorded LTV, and beside it the one the deal's own loan and value
    // imply when they differ (R3 keeps the stated ratio; R4 shows the gap).
    ltv: ltvWithImplied(value('ltv'), value('loan_amount'), get('property_value'), servedImplied),
    ltv_stabilized: formatPoints(value('ltv_stabilized')),
    debt_yield: withheld ? DEBT_YIELD_WITHHELD : formatPoints(value('debt_yield')),
    debt_yield_stabilized: withheld ? DEBT_YIELD_WITHHELD : formatPoints(value('debt_yield_stabilized')),
    dscr: formatMultiple(value('dscr')),
    dscr_stabilized: formatMultiple(value('dscr_stabilized')),
  }

  return {
    displays: {
      ...figures,
      purpose: textOrDash(get('purpose')),
      originated: formatDate(get('originated')),
      priority: textOrDash(get('priority')),
      capital_program: textOrDash(get('capital_program') ?? product),
    },
    derived,
  }
}
